const PREVIEW_SELECTOR = '.app-navigation-preview';
const SLOT_SELECTOR = '[data-slot="popover-content"], [data-slot="tooltip-content"]';

function getPreviewRoot() {
  if (typeof document === 'undefined') return null;
  return document.querySelector(PREVIEW_SELECTOR);
}

/**
 * Marks portaled Radix popover/tooltip content that belongs to the new nav so
 * `_nav_styling.html.erb` can target `body:has(.app-navigation-preview) [data-nav-preview-portal]`
 * without affecting other dashboard overlays.
 */
function tagElementIfNavPreviewPortal(el, preview) {
  if (!(el instanceof Element) || el.hasAttribute('data-nav-preview-portal')) return;

  const slot = el.getAttribute('data-slot');
  const id = el.id;
  if (!id) return;

  if (slot === 'popover-content') {
    const trigger = document.querySelector(`[aria-controls="${CSS.escape(id)}"]`);
    if (trigger && preview.contains(trigger)) el.setAttribute('data-nav-preview-portal', '');
    return;
  }

  if (slot === 'tooltip-content') {
    const trigger = document.querySelector(`[aria-describedby~="${CSS.escape(id)}"]`);
    if (trigger && preview.contains(trigger)) el.setAttribute('data-nav-preview-portal', '');
  }
}

function scanSubtree(root, preview) {
  if (!(root instanceof Element)) return;
  if (root.matches?.(SLOT_SELECTOR)) tagElementIfNavPreviewPortal(root, preview);
  root.querySelectorAll?.(SLOT_SELECTOR).forEach((el) => {
    tagElementIfNavPreviewPortal(el, preview);
  });
}

function subtreeMayHaveSlots(node) {
  if (!(node instanceof Element)) return false;
  if (node.matches?.(SLOT_SELECTOR)) return true;
  return Boolean(node.querySelector?.(SLOT_SELECTOR));
}

function scheduleRetag(run) {
  requestAnimationFrame(() => {
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  });
  setTimeout(run, 0);
}

function recordNeedsRescan(rec, preview) {
  if (rec.type === 'attributes') {
    const name = rec.attributeName;
    if (
      (name === 'aria-describedby' || name === 'aria-controls') &&
      rec.target instanceof Element &&
      preview.contains(rec.target)
    ) {
      return true;
    }
    return false;
  }
  if (rec.type === 'childList') {
    for (const node of rec.addedNodes) {
      if (subtreeMayHaveSlots(node)) return true;
    }
  }
  return false;
}

/** @returns {undefined | (() => void)} */
export function installNavPreviewPortalMarkers() {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
    return undefined;
  }

  const run = () => {
    const preview = getPreviewRoot();
    if (!preview) return;
    scanSubtree(document.body, preview);
  };

  run();

  const observer = new MutationObserver((records) => {
    const preview = getPreviewRoot();
    if (!preview) return;

    let relevant = false;
    for (const rec of records) {
      if (recordNeedsRescan(rec, preview)) {
        relevant = true;
        break;
      }
    }

    if (!relevant) return;

    for (const rec of records) {
      if (rec.type !== 'childList') continue;
      for (const node of rec.addedNodes) {
        if (node instanceof Element) scanSubtree(node, preview);
      }
    }
    run();
    scheduleRetag(run);
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-describedby', 'aria-controls'],
  });

  return () => observer.disconnect();
}
